import { pluralizeEvents, currentMonthAttendedCount } from "@/lib/productLanguage";
import { PageMeta } from "@/components/seo/PageMeta";
import { Users, RefreshCw, Flame, ArrowRight, HelpCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { readProfileSnapshot, writeProfileSnapshot } from "@/utils/profileSnapshot";
import { useGuest } from "@/contexts/GuestContext";
import { GuestActionSheet } from "@/components/ui/GuestActionSheet";
import { useGuestGuard } from "@/hooks/useGuestGuard";
import { useTourStore } from "@/hooks/useTour";

import { scoreEventForPreferences } from "@/lib/preferences";
import { useQuery, useQueryClient, useMutation, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getDay } from "date-fns";
import { useNavigate } from "react-router-dom";
import { BoxBreathingDialog } from "@/components/home/BoxBreathingDialog";
import { BreathingAchievementDialog } from "@/components/breathing/BreathingAchievementDialog";
import { FriendSuggestionsCarousel } from "@/components/social/FriendSuggestionsCarousel";
import { FounderEventsSection } from "@/components/social/FounderEventsSection";
import { SafeAreaSpacer } from "@/components/layout/SafeAreaSpacer";
import { EventAchievementDialog } from "@/components/achievements/EventAchievementDialog";
import { StreakLeaderboard } from "@/components/profile/StreakLeaderboard";
import { MilestoneCelebration } from "@/components/profile/MilestoneCelebration";
import { useStreakMilestone } from "@/hooks/useStreakMilestone";
import { useState, useEffect, useCallback, memo, useRef } from "react";
import { toast } from "sonner";
import { TodayCheckInCard } from "@/components/home/TodayCheckInCard";
import { MyEventsThisWeek } from "@/components/home/MyEventsThisWeek";
import { OnboardingChecklist } from "@/components/home/OnboardingChecklist";
import { PullToRefresh } from "@/components/ui/PullToRefresh";
import { EmailVerificationBanner } from "@/components/home/EmailVerificationBanner";
import { useNotificationIntroToast } from "@/hooks/useNotificationPreferences";
import { parseISO, isToday, isTomorrow } from "date-fns";
import { getCentralTimeDate, formatInCentralTime } from "@/utils/timezone";
import { HomeCowgirlsPromoBanner } from "@/components/home/HomeCowgirlsPromoBanner";
import { NextWellnessPlanCard } from "@/components/home/NextWellnessPlanCard";
import { useFounderEventIds } from "@/hooks/useFounderEvents";

// Move formatEventDate outside component - pure function
const formatEventDate = (dateString: string) => {
  // Get Central Time date for comparison
  const centralDate = getCentralTimeDate(dateString);
  // Create a comparable date object using Central Time values
  const comparableDate = new Date(
    centralDate.getUTCFullYear(),
    centralDate.getUTCMonth(),
    centralDate.getUTCDate(),
    centralDate.getUTCHours(),
    centralDate.getUTCMinutes()
  );
  
  if (isToday(comparableDate)) return `Tonight, ${formatInCentralTime(dateString, 'h:mm a')}`;
  if (isTomorrow(comparableDate)) return `Tomorrow, ${formatInCentralTime(dateString, 'h:mm a')}`;
  return formatInCentralTime(dateString, 'EEE, h:mm a');
};

// Memoized upcoming event card
const UpcomingEventCard = memo(function UpcomingEventCard({ 
  event, 
  index, 
  onClick 
}: { 
  event: any; 
  index: number; 
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className="interactive-surface flex-shrink-0 w-72 h-[296px] bg-card rounded-2xl overflow-hidden border border-border shadow-soft cursor-pointer animate-fade-in"
      style={{ animationDelay: `${index * 75}ms` }}
    >
      <div className="event-media h-36 rounded-none">
        <img 
          src={event.host_logo_url || "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=300&fit=crop"} 
          alt={event.title} 
          loading="lazy"
          className="w-full h-full object-cover" 
        />
      </div>
      <div className="p-4 flex flex-col h-[152px]">
        <div className="flex-1">
          <p className="text-xs font-semibold text-secondary-foreground">{formatEventDate(event.date)}</p>
          <h3 className="font-display font-semibold text-foreground text-[0.95rem] line-clamp-2 min-h-[2.5rem]">{event.title}</h3>
          <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{event.location}{event.distance_mi ? ` · ${event.distance_mi} mi` : ""}</p>
          <p className="mt-1 text-xs font-semibold text-foreground">{event.is_free === false ? (event.cost || 'Paid') : 'Free'}</p>
        </div>
        <Button size="sm" className="w-full mt-auto">
          View event
        </Button>
      </div>
    </div>
  );
});

// Feature flag - set to true to re-enable breathing feature
const SHOW_BREATHING_FEATURE = false;

const Home = () => {
  const { user, isAuthStable } = useAuth();
  const { isGuest } = useGuest();
  const { checkGuestAction, showBlocker, closeBlocker, blockerMessage, intendedAction } = useGuestGuard();
  const { replayTour } = useTourStore();
  const navigate = useNavigate();
  const { data: founderEventIds = [] } = useFounderEventIds();
  const queryClient = useQueryClient();
  
  // Show one-time notification preferences intro toast
  useNotificationIntroToast();
  
  const [breathDialogOpen, setBreathDialogOpen] = useState(false);
  const [achievementDialogOpen, setAchievementDialogOpen] = useState(false);
  const [achievementStreak, setAchievementStreak] = useState(0);
  const [eventAchievementOpen, setEventAchievementOpen] = useState(false);
  const [newEventAchievement, setNewEventAchievement] = useState<'first_event' | 'attended_5' | 'attended_10' | 'attended_25' | 'attended_50' | 'attended_100' | null>(null);
  const [showProfileReady, setShowProfileReady] = useState(false);
  
  // Last known first name, persisted per-user so it survives remounts and
  // cold starts. Prevents the generic "friend" fallback during slow loads.
  const [cachedFirstName, setCachedFirstName] = useState<string | null>(
    () => readProfileSnapshot(user?.id)?.firstName ?? null
  );


  // Read checkInEvent from URL for deep linking from notifications
  const [searchParams, setSearchParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('checkInEvent');
  });

  // Clear the URL parameter after it's been read
  useEffect(() => {
    if (searchParams) {
      const params = new URLSearchParams(window.location.search);
      params.delete('checkInEvent');
      const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchParams]);


  // Fetch user profile.
  // NOTE: never resolve successfully with an empty profile — a cached `null`
  // used to be treated as real data, which is what produced "Hay friend!" and
  // an empty checklist for fully set up accounts. Surface the failure instead
  // so React Query retries.
  const { data: profile, isLoading: profileLoading, isError: profileError } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Profile not found');
      return data;
    },
    enabled: !!user?.id && isAuthStable,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    retry: 2,
  });

  // Persist the user's first name so a slow/failed load never falls back to
  // the generic greeting.
  useEffect(() => {
    const firstName = profile?.full_name?.split(' ')[0] || profile?.username || null;
    if (firstName) {
      setCachedFirstName(firstName);
      writeProfileSnapshot(user?.id, { firstName });
    }
  }, [profile?.full_name, profile?.username, user?.id]);

  // Re-hydrate the snapshot when the signed-in user changes.
  useEffect(() => {
    setCachedFirstName(readProfileSnapshot(user?.id)?.firstName ?? null);
  }, [user?.id]);

  const displayFirstName =
    profile?.full_name?.split(' ')[0] || profile?.username || cachedFirstName;
  // Only show a skeleton while the name is genuinely still unknown. If the
  // request definitively failed we fall through to the neutral greeting
  // rather than spinning forever.
  // Guests (and signed-out visitors) have no profile to wait for, so fall
  // straight through to the neutral greeting instead of an endless skeleton.
  const nameUnknown = !!user?.id && !displayFirstName && !profileError;


  // Check for streak milestones
  const { celebrationMilestone, closeCelebration } = useStreakMilestone(
    profile?.current_streak,
    user?.id
  );
  
  // Check if user just completed onboarding (within last 5 minutes)
  useEffect(() => {
    if (profile?.onboarding_completed_at) {
      const completedTime = new Date(profile.onboarding_completed_at).getTime();
      const now = new Date().getTime();
      const fiveMinutes = 5 * 60 * 1000;
      const isRecent = (now - completedTime) < fiveMinutes;
      const hasSeenBanner = sessionStorage.getItem('profile_ready_banner_shown') === 'true';
      
      if (isRecent && !hasSeenBanner) {
        setShowProfileReady(true);
      }
    }
  }, [profile?.onboarding_completed_at]);

  // Fetch today's daily activities - OPTIMIZED with staleTime and auth stability
  const { data: todayActivity } = useQuery({
    queryKey: ['daily-activity', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      const today = new Date().toISOString().split('T')[0];
      const { data } = await supabase
        .from('user_daily_activities')
        .select('*')
        .eq('user_id', user.id)
        .eq('date', today)
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id && isAuthStable,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  // Fetch breathing streak - OPTIMIZED with staleTime and auth stability
  const { data: breathingStreak = 0 } = useQuery({
    queryKey: ['breathing-streak', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      // Get all breathing activities ordered by date descending
      const { data } = await supabase
        .from('user_daily_activities')
        .select('date, breathe_completed')
        .eq('user_id', user.id)
        .eq('breathe_completed', true)
        .order('date', { ascending: false })
        .limit(100);
      
      if (!data || data.length === 0) return 0;
      
      // Count consecutive days from today/yesterday
      let streak = 0;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      for (let i = 0; i < data.length; i++) {
        const activityDate = new Date(data[i].date);
        activityDate.setHours(0, 0, 0, 0);
        const daysDiff = Math.floor((today.getTime() - activityDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysDiff === i || (i === 0 && daysDiff === 1)) {
          streak++;
        } else {
          break;
        }
      }
      return streak;
    },
    enabled: !!user?.id && isAuthStable,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  // Fetch upcoming free events (tonight + this weekend) - OPTIMIZED with staleTime
  const { data: upcomingEvents = [] } = useQuery({
    queryKey: ['upcoming-events'],
    queryFn: async () => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('is_active', true)
        .eq('is_free', true)
        .gte('date', now)
        .order('date', { ascending: true })
        .limit(20);
      
      if (error) throw error;
      
      // Filter for today + upcoming Saturday and Sunday
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dayOfWeek = getDay(today); // 0 = Sunday, 6 = Saturday
      
      // Calculate next Saturday and Sunday
      let nextSaturday: Date;
      let nextSunday: Date;
      
      if (dayOfWeek === 6) {
        // Today is Saturday
        nextSaturday = new Date(today);
        nextSunday = new Date(today);
        nextSunday.setDate(today.getDate() + 1);
      } else if (dayOfWeek === 0) {
        // Today is Sunday
        nextSaturday = new Date(today);
        nextSaturday.setDate(today.getDate() + 6); // Next Saturday
        nextSunday = new Date(today);
      } else {
        // Weekday: calculate upcoming Sat/Sun
        const daysUntilSaturday = 6 - dayOfWeek;
        nextSaturday = new Date(today);
        nextSaturday.setDate(today.getDate() + daysUntilSaturday);
        nextSunday = new Date(nextSaturday);
        nextSunday.setDate(nextSaturday.getDate() + 1);
      }
      
      return (data || []).filter(event => {
        const eventDate = parseISO(event.date);
        const eventDay = new Date(eventDate);
        eventDay.setHours(0, 0, 0, 0);
        
        // Tonight: any event today
        if (isToday(eventDate)) return true;
        
        // This Weekend: next Saturday or Sunday
        const isSaturday = eventDay.getTime() === nextSaturday.getTime();
        const isSunday = eventDay.getTime() === nextSunday.getTime();
        
        return isSaturday || isSunday;
      }).slice(0, 6); // Limit to 6 events for display
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - events don't change frequently
    gcTime: 10 * 60 * 1000,
  });

  // Check user's RSVPs - OPTIMIZED with staleTime and auth stability
  const { data: myRsvps = [] } = useQuery({
    queryKey: ['my-rsvps', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      const { data } = await supabase
        .from('event_attendances')
        .select('event_id')
        .eq('user_id', user.id)
        .eq('status', 'rsvp');
      return data?.map(a => a.event_id) || [];
    },
    enabled: !!user?.id && isAuthStable,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  // Does the user have any upcoming RSVP at all? Drives Home hierarchy.
  // Read-only: no RSVP/attendance records are written or changed here.
  const { data: nextRsvpedEvent = null, isLoading: upcomingRsvpLoading } = useQuery({
    queryKey: ['home-next-rsvped-event', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('event_attendances')
        .select('event_id, events!inner(*)')
        .eq('user_id', user.id)
        .in('status', ['rsvp', 'attended'])
        .gte('events.date', new Date().toISOString())
        .order('events(date)', { ascending: true })
        .limit(1);
      if (error) throw error;
      return data?.[0]?.events ?? null;
    },
    enabled: !!user?.id && isAuthStable,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
  const hasUpcomingRsvp = !!nextRsvpedEvent;

  // Personalized "start here" events for users with no upcoming RSVP.
  // Preferences only reorder the soonest events — nothing is filtered out.
  const { data: discoverEvents = [], isLoading: discoverEventsLoading } = useQuery({
    queryKey: ['home-discover-events', profile?.id, profile?.neighborhood, profile?.price_preference],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('is_active', true)
        .gte('date', new Date().toISOString())
        .order('date', { ascending: true })
        .limit(24);
      if (error) throw error;

      const candidates = data || [];
      return [...candidates]
        .map((event, index) => ({ event, index, score: scoreEventForPreferences(event, profile) }))
        .sort((a, b) => (b.score - a.score) || (a.index - b.index))
        .slice(0, 3)
        .map((entry) => entry.event);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });




  const handleRsvp = useMutation({
    mutationKey: ["home-event-rsvp"],
    mutationFn: async (eventId: string) => {
      if (!user?.id) throw new Error("Not authenticated");
      const { error } = await supabase.from('event_attendances').insert({
        event_id: eventId,
        user_id: user.id,
        status: 'rsvp',
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-rsvps'] });
      queryClient.invalidateQueries({ queryKey: ['event-attendance'] });
      toast.success('RSVP confirmed!');
    },
  });


  const handleBreathComplete = useCallback(async () => {
    if (!checkGuestAction("Sign in or sign up to track your breathing exercises")) {
      setBreathDialogOpen(false);
      return;
    }
    
    if (!user) return;
    const today = new Date().toISOString().split('T')[0];

    try {
      const { error } = await supabase
        .from('user_daily_activities')
        .upsert({
          user_id: user.id,
          date: today,
          breathe_completed: true
        }, {
          onConflict: 'user_id,date'
        });

      if (error) throw error;
      
      setBreathDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['daily-activity'] });
      queryClient.invalidateQueries({ queryKey: ['breathing-streak'] });
      queryClient.invalidateQueries({ queryKey: ['breathing-achievements'] });
      
      const newStreak = breathingStreak + 1;
      
      // Check for achievement milestones and award them
      if (newStreak === 7 || newStreak === 30 || newStreak === 100) {
        // Call the database function to award achievement
        const { error: achievementError } = await supabase.rpc('award_breathing_achievement', {
          p_user_id: user.id,
          p_streak: newStreak
        });
        
        if (!achievementError) {
          // Show achievement dialog
          setAchievementStreak(newStreak);
          setAchievementDialogOpen(true);
          
          // Show milestone toast
          if (newStreak === 7) {
            toast.success("🎉 Achievement Unlocked: Breathing Warrior!");
          } else if (newStreak === 30) {
            toast.success("🏆 Achievement Unlocked: Zen Master!");
          } else if (newStreak === 100) {
            toast.success("👑 Achievement Unlocked: Mindfulness Champion!");
          }
        }
      } else if (breathingStreak === 6) {
        toast.success("🎉 Amazing! 7 day breathing streak tomorrow!");
      } else if (breathingStreak === 29) {
        toast.success("🏆 One more day until Zen Master!");
      } else if (breathingStreak > 0 && breathingStreak % 7 === 6) {
        toast.success(`🔥 ${breathingStreak + 1} day streak! You're on fire!`);
      } else {
        toast.success("Great job! You took a moment for yourself today 🧘");
      }
    } catch (error) {
      console.error('Error updating breathe activity:', error);
      toast.error("Failed to save your progress. Please try again.");
    }
  }, [checkGuestAction, user, breathingStreak, queryClient]);


  return (
    <PullToRefresh onRefresh={async () => { 
      <PageMeta
        title="Wellness Cowgirls — San Antonio Wellness Community"
        description="Your Wellness Cowgirls dashboard: upcoming San Antonio wellness events, your RSVPs, herds, and friends all in one place."
        path="/"
        jsonLd={[
          { "@context": "https://schema.org", "@type": "Organization", name: "Wellness Cowgirls", url: "https://wellnesscowgirls.com", areaServed: "San Antonio, Texas" },
          { "@context": "https://schema.org", "@type": "WebSite", name: "Wellness Cowgirls", url: "https://wellnesscowgirls.com" },
        ]}
      />
      await queryClient.invalidateQueries({ queryKey: ["profile"] });
      await queryClient.invalidateQueries({ queryKey: ["daily-activity"] });
      await queryClient.invalidateQueries({ queryKey: ["breathing-streak"] });
      await queryClient.invalidateQueries({ queryKey: ["upcoming-events"] });
      await queryClient.invalidateQueries({ queryKey: ["my-rsvps"] });
    }}>
      <div className="page-container bg-background">
        {/* Top Safe Area Spacer - handles device notches and status bar */}
        <SafeAreaSpacer position="top" className="bg-gradient-rose-sky" />
      
      {/* Header */}
      <div className="page-gutter bg-gradient-rose-sky pb-10 pt-5 rounded-b-2xl shadow-soft">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="sr-only">Your Wellness Cowgirls Dashboard</h1>
            <div className="font-display text-[1.75rem] font-semibold text-foreground">
              Hay {nameUnknown ? (
                <Skeleton className="inline-block w-24 h-7 align-middle rounded-md" />
              ) : (
                <span className="animate-fade-in">
                  {displayFirstName || 'friend'}
                </span>
              )}!

            </div>
            {profileLoading ? (
              <Skeleton className="w-28 h-5 mt-1 rounded-md" />
            ) : (
              <div className="flex items-center gap-1 mt-1 animate-fade-in">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-semibold text-foreground">
                  {pluralizeEvents(currentMonthAttendedCount(profile))} Attended This Month
                </span>
              </div>
            )}
          </div>
          <Button aria-label="Replay app tour"
            variant="ghost"
            size="icon"
            onClick={replayTour}
            className="text-foreground/80 hover:text-foreground hover:bg-background/10"
            title="Replay tour"
          >
            <HelpCircle className="w-5 h-5" />
          </Button>
        </div>
        
        <p className="text-foreground/80 text-sm font-medium">
          Small steps, big wins. Let's go together.
        </p>
      </div>

      <div className="page-gutter -mt-5 page-stack">
        {user?.id && !isGuest && (
          <NextWellnessPlanCard
            event={nextRsvpedEvent || discoverEvents[0] || null}
            userId={user.id}
            isRsvped={!!nextRsvpedEvent}
            isMeganGoing={founderEventIds.includes((nextRsvpedEvent || discoverEvents[0])?.id ?? "")}
            isLoading={upcomingRsvpLoading || discoverEventsLoading}
          />
        )}

        {/* Email Verification Banner */}
        <EmailVerificationBanner user={user} />

        {/* Profile Ready Banner */}
        {showProfileReady && (
          <div className="bg-muted/60 border border-border rounded-2xl p-4 card-shadow">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <h3 className="font-bold text-foreground mb-1">🎉 Your profile is ready!</h3>
                <p className="text-sm text-muted-foreground">
                  Start exploring events or customize your goals anytime from your profile.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowProfileReady(false);
                  sessionStorage.setItem('profile_ready_banner_shown', 'true');
                }}
                className="min-w-[44px] min-h-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          </div>
        )}
        
        {/* Your Events — shown first when the user has an upcoming RSVP */}
        {(hasUpcomingRsvp || upcomingRsvpLoading || discoverEvents.length === 0) && (
          <MyEventsThisWeek userId={user?.id} />
        )}

        {/* No upcoming RSVP: lead with a few real upcoming events instead of an empty panel */}
        {!hasUpcomingRsvp && !upcomingRsvpLoading && discoverEvents.length > 0 && (
          <div>
            <div className="mb-4">
              <p className="eyebrow mb-1">Start here</p>
              <h2 className="font-display text-xl font-semibold text-foreground">Upcoming events for you</h2>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth" style={{
              marginLeft: 'calc(-1 * max(1rem, env(safe-area-inset-left)))',
              marginRight: 'calc(-1 * max(1rem, env(safe-area-inset-right)))',
              paddingLeft: 'max(1rem, env(safe-area-inset-left))',
              paddingRight: 'max(1rem, env(safe-area-inset-right))'
            }}>
              {discoverEvents.map((event: any, index: number) => (
                <div key={event.id} className="snap-start">
                  <UpcomingEventCard
                    event={event}
                    index={index}
                    onClick={() => navigate(`/events/${event.id}`)}
                  />
                </div>
              ))}
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/events')}
              className="mt-2 h-11 gap-2 px-0 text-sm font-semibold text-primary hover:bg-transparent"
            >
              Browse all events
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Today's Check-In Events */}
        {user && <TodayCheckInCard userId={user.id} autoOpenEventId={searchParams} />}

        {/* Contextual setup and promotion modules follow the primary event decision. */}
        <OnboardingChecklist />
        <HomeCowgirlsPromoBanner />

        {/* Founder's Events This Week */}
        <FounderEventsSection />

        {/* Tonight & This Weekend (Free) */}
        {upcomingEvents.length > 0 && (
          <div>
            <div className="mb-4">
              <p className="eyebrow mb-1">Happening soon</p>
              <h2 className="font-display text-xl font-semibold text-foreground">Tonight & This Weekend (Free)</h2>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth" style={{
              marginLeft: 'calc(-1 * max(1rem, env(safe-area-inset-left)))',
              marginRight: 'calc(-1 * max(1rem, env(safe-area-inset-right)))',
              paddingLeft: 'max(1rem, env(safe-area-inset-left))',
              paddingRight: 'max(1rem, env(safe-area-inset-right))'
            }}>
              {upcomingEvents.map((event, index) => (
                <div key={event.id} className="snap-start">
                  <UpcomingEventCard
                    event={event}
                    index={index}
                    onClick={() => navigate(`/events/${event.id}`)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Friend Suggestions */}
        <div>
          <FriendSuggestionsCarousel />
        </div>

        {/* Streak Leaderboard */}
        <StreakLeaderboard />



        {/* Take a Breath Section - Hidden temporarily */}
        {SHOW_BREATHING_FEATURE && (
          <div className="bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-5 card-shadow">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-foreground">Take a Breath 🧘</h3>
                <p className="text-sm text-muted-foreground">
                  A moment of calm in your day
                </p>
              </div>
              {todayActivity?.breathe_completed && (
                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  ✓ Done Today
                </Badge>
              )}
            </div>
            
            {/* Breathing Streak Display */}
            {breathingStreak > 0 && (
              <div className="flex items-center gap-2 mb-4 text-sm">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="font-semibold">{breathingStreak} day streak!</span>
              </div>
            )}
            
            <Button
              onClick={() => {
                setBreathDialogOpen(true);
                toast.success("Breathing session started! 🧘", {
                  description: "Take a deep breath and follow the guide"
                });
              }}
              className="w-full bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white"
              size="lg"
              disabled={todayActivity?.breathe_completed}
            >
              {todayActivity?.breathe_completed ? "Completed for Today ✓" : "Take a Moment"}
            </Button>
            
            {todayActivity?.breathe_completed && (
              <p className="text-center text-sm text-muted-foreground mt-3">
                Great job! Come back tomorrow 🌟
              </p>
            )}
          </div>
        )}
      </div>

      {SHOW_BREATHING_FEATURE && (
        <>
          <BoxBreathingDialog
            open={breathDialogOpen} 
            onOpenChange={setBreathDialogOpen}
            onComplete={handleBreathComplete}
          />
          
          <BreathingAchievementDialog
            open={achievementDialogOpen}
            onOpenChange={setAchievementDialogOpen}
            streak={achievementStreak}
            username={profile?.username}
          />
        </>
      )}

      {/* Event Achievement Dialog */}
      {newEventAchievement && (
        <EventAchievementDialog
          open={eventAchievementOpen}
          onOpenChange={setEventAchievementOpen}
          achievementType={newEventAchievement}
          username={profile?.username || 'Cowgirl'}
          earnedDate={new Date().toISOString()}
        />
      )}

      <MilestoneCelebration
        milestone={celebrationMilestone || 0}
        open={celebrationMilestone !== null}
        onOpenChange={closeCelebration}
      />

      <GuestActionSheet 
        open={showBlocker}
        onOpenChange={closeBlocker}
        message={blockerMessage}
        intendedAction={intendedAction}
      />
    </div>
    </PullToRefresh>
  );
};

export default Home;
